import React, { useState, useEffect, ReactNode, createContext } from "react";
import { Modal } from "react-bootstrap";
import BaseButton from "./BaseButton";
import "./css/ModalContentButton.css";

interface ModalContentButtonProps {
    id: string;
    buttonContent: ReactNode;
    modalTitle?: string;
    children: ReactNode;
    className?: string;
    onOpenRequest?: () => void;
    onOpenComplete?: () => void;
    onCloseRequest?: () => void;
}

export const ModalCloseContext = createContext<(() => void) | null>(null);

export const ModalContentButton: React.FC<ModalContentButtonProps> = ({
    id,
    buttonContent,
    modalTitle,
    children,
    className,
    onOpenRequest,
    onOpenComplete,
    onCloseRequest
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const openModal = () => {
        onOpenRequest?.();
        setIsOpen(true);
    };

    const closeModal = (goBack?: boolean | undefined) => {
        onCloseRequest?.();
        setIsOpen(false);
        if (goBack) {
            window.history.back();
        }
    };

    // Fire onOpenComplete after modal animation finishes
    useEffect(() => {
        if (isOpen && onOpenComplete) {
            const timer = setTimeout(() => {
                onOpenComplete();
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [isOpen, onOpenComplete]);

    // Back-button closure support
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handlePop = () => {
            closeModal();
        };

        window.addEventListener("popstate", handlePop);
        window.history.pushState({ modal: true }, "");

        return () => {
            window.removeEventListener("popstate", handlePop);
        };
    }, [isOpen]);


    return (
        <>
            <BaseButton
                ariaLabel={id || "Open Content modal"}
                className={`modal-content-button ${isOpen === true ? ' active' : ''} `}
                onClick={openModal}>
                {buttonContent}
            </BaseButton>

            <Modal
                show={isOpen}
                onHide={() => { closeModal(true); }}
                className={className}
                backdrop
                keyboard
                centered>

                <Modal.Header closeButton>
                    {modalTitle && (
                        <Modal.Title>{modalTitle}</Modal.Title>
                    )}
                </Modal.Header>

                <ModalCloseContext.Provider value={closeModal}>
                    <Modal.Body>
                        {children}
                    </Modal.Body>
                </ModalCloseContext.Provider>
            </Modal>
        </>
    );
};

export default ModalContentButton;
